import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../utils/useCart';
import Inner from '../../components/Inner';
import Title from '../../components/Title';
import Button from '../../components/Button';
import { useConvertPrice } from '../../utils/convertPrice';
import { useRecoilValue } from 'recoil';
import { selectedCurrencyState } from '../../store/selectedCurrencyAtom';
import { promoCodeApi } from '../../api';
import { loadStripe } from '@stripe/stripe-js';
import { apiClient } from '../../api/api';
import { currencySigns } from '../../utils/currencies';
import Select from "react-select";
import countryList from 'react-select-country-list';
import { Truck } from 'lucide-react';
import { stripeKey } from '../../config';

const stripePromise = loadStripe(stripeKey);

function Cart() {
    const { t } = useTranslation();
    const { cart, removeFromCart } = useCart();
    const [loading, setLoading] = useState(false);
    const convertPrice = useConvertPrice();
    const selectedCurrency = useRecoilValue(selectedCurrencyState);

    const countryOptions = countryList().getData();

    const [promoCode, setPromoCode] = useState('');
    const [promoCodeMessage, setPromoCodeMessage] = useState('');
    const [discount, setDiscount] = useState(0); 
    
    const [deliveryError, setDeliveryError] = useState('');
    const [invalidFields, setInvalidFields] = useState([]);
    const [checkoutError, setCheckoutError] = useState('');

    const requiredFields = [
        'first_name',
        'last_name',
        'email',
        'country_code',
        'region',
        'city',
        'postal_code',
        'street',
        'phone'
    ];

    const isEmailValid = (email) => {
    // Simple email regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};


    const isDeliveryDataValid = () => {
        const invalid = requiredFields.filter(
            field => !deliveryData[field] || deliveryData[field].trim() === ''
        );
        if (deliveryData.email && !isEmailValid(deliveryData.email)) {
            if (!invalid.includes('email')) invalid.push('email');
        }
        setInvalidFields(invalid);
        return invalid.length === 0;
    };

    const [deliveryData, setDeliveryData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        country_code: '',
        region: '',
        city: '',
        postal_code: '',
        street: '',
        apartment: '',
        phone: '',
    });

    // Helper to get shipping price for an item (in selected currency)
    // and update localStorage so it won't be calculated multiple times
    const getShippingPrice = (item) => {
        let shippingPrice = 0;

        if (!item.shippingInfo || !item.shippingInfo.price) {
            shippingPrice = 0;
        } else if (item.shippingInfo.currency === selectedCurrency) {
            shippingPrice = Number(item.shippingInfo.price);
        } else if (item.shippingInfo.currency === 'EUR') {
            shippingPrice = Number(convertPrice(item.shippingInfo.price));
        } else {
            shippingPrice = Number(item.shippingInfo.price);
        }

        return shippingPrice;
    };

    // Calculate total shipping for all items
    const totalShipping = useMemo(() => {
        return cart.reduce((sum, item) => sum + getShippingPrice(item), 0);
    }, [cart, selectedCurrency, convertPrice]);


    const totalItemsSum = useMemo(() => {
        const itemsSum = cart.reduce((sum, item) => sum + Number(convertPrice(item.price_eur, item.discount)), 0);
        const discounted = itemsSum - (itemsSum * discount) / 100;
        return discounted
    }, [cart, convertPrice, discount])


    // Calculate total sum (items + shipping - discount)
    const totalSum = useMemo(() => {
        return totalItemsSum + totalShipping;
    }, [cart, convertPrice, discount, totalShipping]);


    const handlePromoCodeApply = async () => {
        try {
            const promo = await promoCodeApi.show(promoCode);

            if (promo && promo.is_active) {
                setDiscount(promo.discount_pc);
                setPromoCodeMessage(t('Promo code applied successfully!'));
            } else {
                setPromoCodeMessage(t('Promo code is invalid or expired.'));
            }
        } catch (error) {
            console.error('Error applying promo code:', error);
            setPromoCodeMessage(t('Promo code is invalid or expired.'));
        }
    };

    const handleDeliveryDataChange = (e) => {
        const { name, value } = e.target;
        setDeliveryData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleCheckout = async () => {
        setDeliveryError('');
        setInvalidFields([]);
        setCheckoutError('');

        if (!isDeliveryDataValid()) {
            setDeliveryError(t('Please fill in all required delivery fields.'));
            return;
        }

        setLoading(true);

        try {
            const stripe = await stripePromise;
            
            const cartItems = cart.map((item) => ({
                id: item.id,
                shipping_info: {
                    price: getShippingPrice(item),
                    currency: item.shippingInfo.currency,
                    country_code: item.shippingInfo.countryCode
                }
            }));

            const payload = {
                cart_items: cartItems,
                delivery_address: deliveryData,
                promo_code: promoCode,
                total_amount: totalItemsSum,
                currency: selectedCurrency,
            }

            const response = await apiClient.post('/create-checkout-session/', payload);

            const session = response.data;

            const result = await stripe.redirectToCheckout({
                sessionId: session.id,
            });

            if (result.error) {
                console.error(result.error.message);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || t('An error occurred during checkout.');
            setCheckoutError(errorMsg);
            console.error('Error during checkout:', error.response?.data || error.message);
        } finally {
            setLoading(false); // Stop loading if error occurs
        }
    };

    return (
        <>
            <Title>{t('Shopping Cart')}</Title>
            <Inner>
                {/* Overlay spinner */}
                {loading && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                        <div className="bg-white p-8 rounded shadow flex flex-col items-center">
                            <svg className="animate-spin h-8 w-8 text-black mb-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                            </svg>
                            <span className="text-lg">{t('Processing your order...')}</span>
                        </div>
                    </div>
                )}
                <h1 className="text-5xl py-6">{t('Shopping Cart')}</h1>
                {cart.length === 0 ? (
                    <p className="text-xl">{t('Your cart is empty.')}</p>
                ) : (
                    <div className="flex flex-col gap-8">
                        {/* Cart Items */}
                        <div className="flex flex-col gap-4">
                            {cart.map((item) => {
                                const originalPrice = convertPrice(item.price_eur);
                                const hasItemDiscount = item.discount && Number(item.discount) > 0;
                                const discountedPrice = hasItemDiscount
                                    ? convertPrice(item.price_eur, item.discount)
                                    : originalPrice;
                                const shippingPrice = getShippingPrice(item);
                                return (
                                    <div
                                        key={item.id}
                                        className="flex justify-between items-center pb-4"
                                    >
                                        <div className="flex items-center gap-4">
                                            <img
                                                src={item.painting_urls?.[0]?.[1]}
                                                alt={item.title}
                                                className="w-20 h-20 object-cover border border-gray-400"
                                            />
                                            <div>
                                                <h2 className="text-xl">{item.title}</h2>
                                                <p className="text-lg font-light">
                                                    {hasItemDiscount ? (
                                                        <>
                                                            <span className="line-through text-slate-400 mr-2">
                                                                {currencySigns[selectedCurrency]} {originalPrice}
                                                            </span>
                                                            <span className="text-black">
                                                                {currencySigns[selectedCurrency]} {discountedPrice}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {currencySigns[selectedCurrency]} {originalPrice}
                                                        </>
                                                    )}
                                                </p>
                                                {shippingPrice > 0 && (
                                                    <p className="text-sm text-slate-500">
                                                        <Truck className="w-4 h-4 inline-block" />
                                                        {" "} {selectedCurrency} {shippingPrice}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            type="secondary"
                                            title={t('Remove')}
                                            styles="bg-white text-black border border-black hover:bg-black hover:text-white transition"
                                            onClick={() => removeFromCart(item.id)}
                                            disabled={loading}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Shipping Total */}
                        <div className="flex justify-between items-center border-t pt-4">
                            <h2 className="text-2xl">{t('Shipping cost')}</h2>
                            <p className="text-2xl font-light">
                                {currencySigns[selectedCurrency]} {totalShipping}
                            </p>
                        </div>

                        {/* Total Sum */}
                        <div className="flex justify-between items-center border-slate-200 pt-4">
                            <h2 className="text-2xl">{t('Total')}</h2>
                            <p className="text-2xl font-light">
                                {currencySigns[selectedCurrency]} {totalSum.toFixed(2)}
                            </p>
                        </div>

                        {/* Promo Code Section */}
                        <div className="flex flex-col gap-4 border-t border-slate-200 pt-4">
                            <h2 className="text-2xl">{t('Promo Code')}</h2>
                            <div className="flex gap-4">
                                <input
                                    type="text"
                                    value={promoCode}
                                    onChange={(e) => setPromoCode(e.target.value)}
                                    placeholder={t('Enter promo code')}
                                    className="w-full border border-slate-200 p-2 rounded"
                                    disabled={loading}
                                />
                                <Button
                                    type="primary"
                                    title={t('Apply')}
                                    styles="bg-black text-white hover:bg-gray-800 transition"
                                    onClick={handlePromoCodeApply}
                                    disabled={loading}
                                />
                            </div>
                            {promoCodeMessage && (
                                <p className="text-sm text-slate-400">{promoCodeMessage}</p>
                            )}
                        </div>

                        {/* Delivery Data Section */}
                        <div className="flex flex-col gap-4 border-t border-slate-200 pt-4">
                            <h2 className="text-2xl">{t('Delivery Information')}</h2>
                            <div className="flex flex-col gap-4">
                                {/* First Name */}
                                <input
                                    type="text"
                                    name="first_name"
                                    value={deliveryData.first_name || ''}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('First Name') + ' *'}
                                    className={`w-full border p-2 rounded ${invalidFields.includes('first_name') ? 'border-red-500' : 'border-slate-200'}`}
                                    required
                                />
                                {/* Last Name */}
                                <input
                                    type="text"
                                    name="last_name"
                                    value={deliveryData.last_name || ''}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('Last Name') + ' *'}
                                    className={`w-full border p-2 rounded ${invalidFields.includes('last_name') ? 'border-red-500' : 'border-slate-200'}`}
                                    required
                                />
                                {/* Email */}
                                <input
                                    type="email"
                                    name="email"
                                    value={deliveryData.email || ''}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('Email') + ' *'}
                                    className={`w-full border p-2 rounded ${invalidFields.includes('email') ? 'border-red-500' : 'border-slate-200'}`}
                                    required
                                />
                                {/* Country */}
                                <Select
                                    name="country"
                                    options={countryOptions}
                                    value={countryOptions.find(option => option.value === deliveryData.country_code) || null}
                                    onChange={option =>
                                        setDeliveryData(prev => ({
                                            ...prev,
                                            country_code: option ? option.value : ''
                                        }))
                                    }
                                    placeholder={t('Select Country') + ' *'}
                                    classNamePrefix="react-select"
                                    styles={invalidFields.includes('country_code') ? { control: (base) => ({ ...base, borderColor: '#ef4444' }) } : {}}
                                    isClearable
                                />
                                {/* Region / State / Province */}
                                <input
                                    type="text"
                                    name="region"
                                    value={deliveryData.region || ''}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('Region / State / Province') + ' *'}
                                    className={`w-full border p-2 rounded ${invalidFields.includes('region') ? 'border-red-500' : 'border-slate-200'}`}
                                    required
                                />
                                {/* City / Town */}
                                <input
                                    type="text"
                                    name="city"
                                    value={deliveryData.city || ''}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('City / Town') + ' *'}
                                    className={`w-full border p-2 rounded ${invalidFields.includes('city') ? 'border-red-500' : 'border-slate-200'}`}
                                    required
                                />
                                {/* Postal / ZIP Code */}
                                <input
                                    type="text"
                                    name="postal_code"
                                    value={deliveryData.postal_code || ''}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('Postal / ZIP Code') + ' *'}
                                    className={`w-full border p-2 rounded ${invalidFields.includes('postal_code') ? 'border-red-500' : 'border-slate-200'}`}
                                    required
                                />
                                {/* Street Address */}
                                <input
                                    type="text"
                                    name="street"
                                    value={deliveryData.street || ''}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('Street Address – Street name and number') + ' *'}
                                    className={`w-full border p-2 rounded ${invalidFields.includes('street') ? 'border-red-500' : 'border-slate-200'}`}
                                    required
                                />
                                {/* Apartment / Unit / Floor */}
                                <input
                                    type="text"
                                    name="apartment"
                                    value={deliveryData.apartment || ''}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('Apartment / Unit / Floor') + ` (${t('optional')})`}
                                    className="w-full border border-slate-200 p-2 rounded"
                                />
                                {/* Phone */}
                                <input
                                    type="text"
                                    name="phone"
                                    value={deliveryData.phone}
                                    onChange={handleDeliveryDataChange}
                                    placeholder={t('Phone Number') + ' *'}
                                    className={`w-full border p-2 rounded ${invalidFields.includes('phone') ? 'border-red-500' : 'border-slate-200'}`}
                                    required
                                />
                            </div>
                            {deliveryError && (
                                <p className="text-red-500 text-sm">{deliveryError}</p>
                            )}
                        </div>
                        {/* Checkout Button */}
                        <div className="flex flex-col items-center md:justify-center">
                            {checkoutError && (
                                <p className="text-red-500 text-sm mb-2">{checkoutError}</p>
                            )}
                            {/* Why styles in button below instead of className? */}
                            <Button
                                type="primary"
                                title={t('Checkout')}
                                styles="w-full max-w-xs md:max-w-none md:w-[50%] bg-black text-white hover:bg-gray-800 transition text-lg py-4"
                                onClick={handleCheckout}
                                disabled={loading}
                            />
                        </div>
                    </div>
                )}
            </Inner>
        </>
    );
}

export default Cart;